import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../../environments/environment';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsInitializerService {
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private logger: LoggerService,
  ) {
    // Delay initialization to ensure it happens after Angular hydration is complete
    if (isPlatformBrowser(this.platformId)) {
      // Use setTimeout to push initialization to the next tick
      setTimeout(() => {
        this.initGoogleAnalytics();
      }, 0);
    }
  }

  private initGoogleAnalytics(): void {
    // Only initialize in browser environment
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      const measurementId = environment?.firebase?.measurementId;
      if (!measurementId) {
        this.logger.warn('Google Analytics measurement ID is missing');
        return;
      }

      // Add Google Analytics script dynamically
      const scriptId = 'ga-gtag';
      if (document.getElementById(scriptId)) {
        return; // Script already added
      }

      // Create gtag script
      const gtagScript = document.createElement('script');
      gtagScript.id = scriptId;
      gtagScript.async = true;
      gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      document.head.appendChild(gtagScript);

      // Initialize dataLayer and gtag function
      window.dataLayer = window.dataLayer || [];
      function gtag(...args: any[]): void {
        window.dataLayer.push(arguments);
      }
      window.gtag = gtag;

      // Initialize gtag
      gtag('js', new Date());
      gtag('config', measurementId, {
        debug_mode: !environment.production,
        send_page_view: false, // Let Angular handle page views
      });
    } catch (error) {
      this.logger.error('Error initializing Google Analytics:', error);
    }
  }
}

// Declare global gtag function
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}
